import { useMemo } from "react";
import { useFeatureFlags } from "@/query/hooks/useFeatureFlags";
import type { DisabledColumnsConfig } from "../utils/sanitizeDisabledColumns";
import { useRecapModeOptional } from "../contexts/RecapContext";

/**
 * Derives the disabled-column configuration from feature flags.
 *
 * When a column is feature-flag-disabled the user cannot see or edit it,
 * so its flow-level value must be treated as null — it must not trigger
 * validation warnings and must not leak to ERPs.
 *
 * Mirrors the visibility rules in `useRecapColumns` and
 * `useConfirmWarnings` so all three surfaces agree.
 */
export function useDisabledColumnsConfig(): DisabledColumnsConfig {
  const { data: flags, isLoading } = useFeatureFlags();
  const mode = useRecapModeOptional();

  return useMemo<DisabledColumnsConfig>(() => {
    // While flags are loading, treat optional columns as ENABLED so we
    // never accidentally nullify data for companies that use them. The
    // tradeoff: a confirm during the brief loading window skips sanitization
    // (stale data might leak), but that's safer than irreversible data loss.
    // Flags are TanStack-cached and resolve almost instantly after first load.
    if (isLoading) {
      return {
        // Transport (mode === null) structurally lacks the unit column, so
        // unit must be disabled even while flags load — no false-negative.
        unitDisabled: mode === null,
        weightDisabled: false,
        priceDisabled: false,
        m3CommentsDisabled: false,
      };
    }

    // Transport (mode === null) never shows the unit column regardless of the
    // flag — unit must always be sanitized (nulled) before ERP export.
    const unitEnabled = mode !== null && flags?.showUnitRecap !== false;
    const weightEnabled = flags?.showWeightRecap !== false;
    const priceEnabled = flags?.showPricesRecap !== false;
    const isVivoMode = (flags?.export_mode as string | undefined) === "vivo";

    return {
      unitDisabled: !unitEnabled,
      weightDisabled: !weightEnabled,
      priceDisabled: !priceEnabled,
      m3CommentsDisabled: !isVivoMode,
    };
  }, [flags, isLoading, mode]);
}
